import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import Button from '../../../components/atoms/forms/Button';
import Checkbox from '../../../components/atoms/forms/Checkbox';
import useRouteEdits from './useRouteEdits';
import useRouteVehicleEdits from './useRouteVehicleEdits';
import RouteVehicleHeader from './RouteVehicleHeader';
import StopList from './StopList';
import RouteStopsMap from './RouteStopsMap';
import SegmentedToggle from '../../../components/atoms/SegmentedToggle';
import PinStopModal, { PIN_INTENT_WINDOW } from './PinStopModal';
import usePinModal from './usePinModal';
import useBulkQueue from './useBulkQueue';
import RemoveStopsModal from './RemoveStopsModal';
import UpdateRouteModal from './UpdateRouteModal';
import { orderStopsBySequence, getStopRowKey } from './utils';
import { anchorWindowDisplay } from './windowDisplay';
import routeCardBase from '../routeCardStyles';

// One card per active route. Owns its own dirty state via useRouteEdits and
// persists drafts to localStorage. On Update Route confirm, hands a
// serialized payload back to the parent — the parent owns the
// createNextmvRun call + receipt strip lifecycle.

const DEFAULT_CAPACITY = 70;

const StyledDiv = styledComponent('div', {
  ...routeCardBase('rc'),

  '&.is-locked': {
    opacity: 0.85,
  },
  // Brief focus ring after "Go to Route Editor →" scrolls the supervisor
  // here from the Missed Stops Review section. Decays back to no border so
  // the card returns to its normal look once the supervisor's eye lands.
  '&.is-focus-flash': {
    animation: 'rc-focus-flash 2s ease-out',
  },
  '@keyframes rc-focus-flash': {
    '0%': { boxShadow: `0 0 0 4px ${theme.colors.infoBlue}66` },
    '100%': { boxShadow: '0 0 0 0 transparent' },
  },

  '& .rc-sync-badge': {
    fontSize: theme.typography.hint.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
    background: theme.colors.bgMuted,
    padding: theme.spacing.smallBadgePadding,
    borderRadius: theme.radii.input,
  },
  '& .rc-expand': {
    color: theme.colors.textMuted,
    fontSize: theme.sizes.smallFontSize,
    background: 'transparent',
    border: 'none',
    padding: '4px 8px',
    cursor: 'pointer',
    borderRadius: theme.radii.input,
  },
  '& .rc-expand:hover': { background: theme.colors.bgMuted },
  '& .rc-expand:focus': {
    outline: 'none',
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
  '& .rc-body': { padding: theme.spacing.bodyPadding },
  '& .rc-toolbar': {
    display: 'flex',
    gap: theme.spacing.fieldGap,
    marginBottom: theme.spacing.fieldGap,
    flexWrap: 'wrap',
  },
  '& .rc-toolbar-btn': {
    padding: theme.spacing.smallButtonPadding,
    background: theme.colors.bgCard,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radii.input,
    fontSize: theme.typography.label.fontSize,
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.text,
    cursor: 'pointer',
    transition: theme.transitions.default,
  },
  // "Armed" state — stops are queued and the button is ready to fire.
  // Tinted red to mirror the destructive intent and draw the supervisor's
  // eye after they check the per-row delete checkboxes (otherwise it's easy
  // to miss the small toolbar button at the top of the route).
  '& .rc-toolbar-btn.is-armed': {
    background: theme.colors.dangerBg,
    borderColor: theme.colors.danger,
    color: theme.colors.danger,
  },
  '& .rc-toolbar-btn.is-armed:hover:not(:disabled)': {
    background: theme.colors.dangerHoverBg,
  },
  '& .rc-toolbar-btn:disabled': {
    color: theme.colors.disabled,
    cursor: 'not-allowed',
  },
  '& .rc-footer': {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing.fieldGap,
    padding: theme.spacing.bodyPadding,
    borderTop: `1px solid ${theme.colors.borderLight}`,
    background: theme.colors.bgMuted,
  },
});

function RouteCard({
  route,
  dropOffDate,
  isSelectedForConsolidation,
  isSyncing,
  isEditorLocked,
  isLead,
  onToggleConsolidationSelection,
  onUpdateConfirmed,
  onClearMissedStopsForRoute,
}) {
  const stops = useMemo(() => route.stops || [], [route.stops]);
  const routeName = route.routeName || route.route || 'Route';

  const persistKey = useMemo(
    () => ({
      dropOffDate,
      routeName,
      previousRunId: route.previousRunId || null,
    }),
    [dropOffDate, routeName, route.previousRunId],
  );

  const edits = useRouteEdits({ stops, persistKey });

  // Map view shows stops in the same order the list displays them (anchors +
  // pending sequence), not raw stockingIndex order. Anchored stops also need
  // their window display overridden the same way StopList does — otherwise
  // the map tooltip shows the stale NextMV-calculated window instead of the
  // supervisor-pinned one.
  const mapStops = useMemo(
    () =>
      orderStopsBySequence(stops, edits.displayedSequence).map((s) => {
        const anchor = edits.anchoredStops.get(getStopRowKey(s));
        if (!anchor) return s;
        const anchorDisplay = anchorWindowDisplay(s, anchor);
        return {
          ...s,
          startTimeWindowDisplay: anchorDisplay.startTimeWindowDisplay,
          endTimeWindowDisplay: anchorDisplay.endTimeWindowDisplay,
        };
      }),
    [stops, edits.displayedSequence, edits.anchoredStops],
  );

  // Vehicle assignment header — surfaces shift times for the assigned
  // vehicle on this route. Shift/target edits arm Update Route and are
  // pushed to NextMV as vehicle overrides on the reroute (see
  // RouteOperationsTab.handleRouteUpdateConfirmed). Sourced from the staged
  // run (matched to this route by vehicle id) since locationRoutes doesn't
  // carry fleet data; null → RouteVehicleHeader renders nothing.
  const firstVehicle = route.vehicles?.[0] || null;
  const vehicleEdits = useRouteVehicleEdits(routeName, firstVehicle);

  const [isExpanded, setIsExpanded] = useState(true);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [pendingDeleteRow, setPendingDeleteRow] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const {
    pendingPin,
    requestPinFromDrop,
    requestPinFromRow,
    submitPin,
    cancelPin,
  } = usePinModal(edits);
  const {
    bulkQueue,
    bulkQueuedStops,
    toggle: handleToggleBulkQueue,
    clear: clearBulkQueue,
  } = useBulkQueue(edits.stopByKey);

  // isSyncing = THIS route's reroute is in flight (shows the badge).
  // isEditorLocked = ANY reroute is in flight — a createNextmvRun
  // regenerates the whole staged run, so every card goes read-only.
  const isReadOnly = isSyncing || isEditorLocked;

  const toteTotal = useMemo(() => {
    if (typeof route.toteCountPerRoute === 'number')
      return route.toteCountPerRoute;
    return stops.reduce((acc, s) => acc + (Number(s.toteCountPerStop) || 0), 0);
  }, [route.toteCountPerRoute, stops]);
  const capacity = route.capacity || DEFAULT_CAPACITY;

  const handleUndoRemove = useCallback(
    (stopKey) => edits.toggleRemove(stopKey, false),
    [edits.toggleRemove],
  );

  // --- Remove modal ---

  const handleOpenRemoveModal = () => {
    if (bulkQueuedStops.length === 0) return;
    setPendingDeleteRow(null);
    setShowRemoveModal(true);
  };
  const handleRequestRowDelete = (stop, stopKey) => {
    setPendingDeleteRow({ stop, stopKey });
    setShowRemoveModal(true);
  };
  const handleRemoveModalSubmit = (reasonsByStopKey) => {
    if (pendingDeleteRow) {
      const reason = reasonsByStopKey[pendingDeleteRow.stopKey];
      edits.toggleRemove(pendingDeleteRow.stopKey, true, reason);
    } else {
      bulkQueuedStops.forEach(({ stopKey }) => {
        edits.toggleRemove(stopKey, true, reasonsByStopKey[stopKey]);
      });
      clearBulkQueue();
    }
    setPendingDeleteRow(null);
    setShowRemoveModal(false);
  };
  const handleRemoveModalCancel = () => {
    setPendingDeleteRow(null);
    setShowRemoveModal(false);
  };

  // --- Update Route + Cancel ---

  const handleOpenUpdateModal = () => {
    if (!isRouteDirty || vehicleEdits.validationError) return;
    setShowUpdateModal(true);
  };
  const handleUpdateConfirm = () => {
    onUpdateConfirmed({
      routeName,
      payload: {
        ...edits.serialize(),
        vehicleOverride: vehicleEdits.isDirty ? vehicleEdits.serialize() : null,
      },
    });
    // Don't reset here — RouteOperationsTab clears the draft when polling
    // confirms the new run. Resetting now would lose the optimistic state
    // if the mutation fails before the polling tick.
    setShowUpdateModal(false);
  };
  const handleUpdateCancel = () => setShowUpdateModal(false);

  // Pending missed stops staged onto THIS route, derived from the prop
  // stops list (synthesized in RouteOperationsTab from the supervisor's
  // Missed Stops Review assignments).
  const pendingMissedStops = useMemo(
    () =>
      stops
        .filter((s) => s.isMissedStopPending)
        .map((s) => ({
          rowKey: s.missedStopRowKey || s.id,
          stopId: s.id,
          originalRoute: s.missedStopOriginalRoute,
          originalDate: s.missedStopOriginalDate,
        })),
    [stops],
  );
  const hasPendingMissedStop = pendingMissedStops.length > 0;
  // Route is dirty if either the edits hook says so OR there's a ghost
  // row waiting to be confirmed via Update Route.
  const isRouteDirty =
    edits.isDirty || vehicleEdits.isDirty || hasPendingMissedStop;

  const handleCardCancel = () => {
    edits.reset();
    vehicleEdits.reset();
    if (hasPendingMissedStop) {
      onClearMissedStopsForRoute(routeName);
    }
  };

  // Used by UpdateRouteModal — stable arrays/object for the keys it iterates.
  const anchoredKeys = useMemo(
    () => [...edits.anchoredStops.keys()],
    [edits.anchoredStops],
  );
  const removeSelectionKeys = useMemo(
    () => [...edits.removeSelections.keys()],
    [edits.removeSelections],
  );

  return (
    <StyledDiv
      className={isReadOnly ? 'is-locked' : ''}
      data-testid={`route-card-${routeName}`}
    >
      <div className="rc-header">
        {!isLead && (
          <Checkbox
            checked={isSelectedForConsolidation}
            onChange={onToggleConsolidationSelection}
            disabled={isReadOnly}
            aria-label={`Select ${routeName} for consolidation`}
            title={
              (isReadOnly &&
                'Routes are re-optimizing — wait for the current run to finish') ||
              (isSelectedForConsolidation
                ? `Unselect ${routeName} from the consolidation batch`
                : `Add ${routeName} to the batch — use "Consolidate Selected" to merge with other checked routes via NextMV`)
            }
            dataTestId={`route-card-consolidation-checkbox-${routeName}`}
          />
        )}
        <div className="rc-title" title={routeName}>
          {routeName}
        </div>
        <RouteVehicleHeader
          vehicle={firstVehicle}
          shiftStart={vehicleEdits.shiftStart}
          shiftEnd={vehicleEdits.shiftEnd}
          targetShiftEnd={vehicleEdits.targetShiftEnd}
          onShiftStartChange={vehicleEdits.setShiftStart}
          onShiftEndChange={vehicleEdits.setShiftEnd}
          onTargetShiftEndChange={vehicleEdits.setTargetShiftEnd}
          readOnly={isLead}
          validationError={vehicleEdits.validationError || ''}
        />
        <div className="rc-stat">
          Totes <strong>{toteTotal}</strong> / {capacity}
        </div>
        {isSyncing && (
          <span className="rc-sync-badge" data-testid="rc-sync-badge">
            Syncing reroute…
          </span>
        )}
        <button
          type="button"
          className="rc-expand"
          onClick={() => setIsExpanded((v) => !v)}
          aria-label={isExpanded ? 'Collapse route' : 'Expand route'}
          aria-expanded={isExpanded}
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>

      {isExpanded && (
        <div className="rc-body">
          <div className="rc-toolbar">
            <div className="rc-view-toggle" data-testid="rc-view-toggle">
              <SegmentedToggle
                options={[
                  { value: 'list', label: 'List' },
                  { value: 'map', label: 'Map' },
                ]}
                value={viewMode}
                onChange={setViewMode}
                ariaLabel="Route view"
                size="sm"
              />
            </div>
            {viewMode === 'list' && !isLead && (
              <button
                type="button"
                className={`rc-toolbar-btn${
                  bulkQueuedStops.length > 0 ? ' is-armed' : ''
                }`}
                onClick={handleOpenRemoveModal}
                disabled={isReadOnly || bulkQueuedStops.length === 0}
                data-testid="rc-bulk-remove-btn"
              >
                × Remove selected ({bulkQueuedStops.length})
              </button>
            )}
          </div>
          {viewMode === 'map' ? (
            <RouteStopsMap stops={mapStops} />
          ) : (
            <StopList
              stops={stops}
              displayedSequence={edits.displayedSequence}
              anchoredStops={edits.anchoredStops}
              removeSelections={edits.removeSelections}
              bulkQueue={bulkQueue}
              isLocked={isReadOnly}
              isViewOnly={isLead}
              onDropNeighbor={requestPinFromDrop}
              onToggleBulkQueue={handleToggleBulkQueue}
              onUndoRemove={handleUndoRemove}
              onPinClick={requestPinFromRow}
              onRequestDelete={handleRequestRowDelete}
            />
          )}
        </div>
      )}

      {isExpanded && !isLead && (
        <div className="rc-footer">
          <Button
            variant="secondary"
            onClick={handleCardCancel}
            disabled={isReadOnly || !isRouteDirty}
            dataTestId="rc-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleOpenUpdateModal}
            disabled={
              isReadOnly ||
              !isRouteDirty ||
              Boolean(vehicleEdits.validationError)
            }
            dataTestId="rc-update-btn"
          >
            Update Route
          </Button>
        </div>
      )}

      <PinStopModal
        show={pendingPin !== null}
        stopName={
          pendingPin
            ? pendingPin.stop.displayName ||
              pendingPin.stop.id ||
              pendingPin.stop.stop
            : ''
        }
        prefill={pendingPin ? pendingPin.prefill : null}
        intent={pendingPin ? pendingPin.intent : PIN_INTENT_WINDOW}
        onSubmit={submitPin}
        onCancel={cancelPin}
      />

      <RemoveStopsModal
        show={showRemoveModal}
        selectedStops={pendingDeleteRow ? [pendingDeleteRow] : bulkQueuedStops}
        initialReasons={null}
        onSubmit={handleRemoveModalSubmit}
        onCancel={handleRemoveModalCancel}
      />

      <UpdateRouteModal
        show={showUpdateModal}
        routeName={routeName}
        stopByKey={edits.stopByKey}
        anchoredStops={edits.anchoredStops}
        anchoredKeys={anchoredKeys}
        removeSelections={edits.removeSelections}
        removeSelectionKeys={removeSelectionKeys}
        pendingMissedStops={pendingMissedStops}
        onConfirm={handleUpdateConfirm}
        onCancel={handleUpdateCancel}
      />
    </StyledDiv>
  );
}

RouteCard.propTypes = {
  route: PropTypes.shape({
    routeName: PropTypes.string,
    route: PropTypes.string,
    stops: PropTypes.array,
    toteCountPerRoute: PropTypes.number,
    capacity: PropTypes.number,
    previousRunId: PropTypes.string,
  }).isRequired,
  dropOffDate: PropTypes.string.isRequired,
  isSelectedForConsolidation: PropTypes.bool,
  isSyncing: PropTypes.bool,
  isEditorLocked: PropTypes.bool,
  isLead: PropTypes.bool,
  onToggleConsolidationSelection: PropTypes.func.isRequired,
  onUpdateConfirmed: PropTypes.func.isRequired,
  onClearMissedStopsForRoute: PropTypes.func,
};

RouteCard.defaultProps = {
  isSelectedForConsolidation: false,
  isSyncing: false,
  isEditorLocked: false,
  isLead: false,
  onClearMissedStopsForRoute: () => {},
};

export default RouteCard;
